import React, { ReactElement, useState, useCallback } from "react";
import { ResponsiveLine, LineSvgProps } from "@nivo/line";

import { format } from "date-fns";

import { TimeSelection, createLineGraphConfig, ParsedDataSpan } from "./data";

import PricingDataLoader from "./PricingDataLoader";
import { GetIexQuote } from "$gql/queries/currentPricing/GetIexQuote.gen";
import { useQueryBundle } from "source/hooks";
import ChartWrapper from "./ChartWrapper";
import LoadingBlock from "components/Loading/LoadingBlock";
import { stockIncreaseColor, stockDecreaseColor } from "styles/theme";

import styles from "./PerformanceGraph.module.scss";
import { customCurrencyFormat, formatCurrency } from "@tiicker/util/lib/formats";

interface Props {
  tickerSymbol: string;
  inline?: boolean;
  inlineSwitchCallback?(newValue: boolean): void;
}

type PricingStateType = {
  [key in TimeSelection]: ParsedDataSpan | null;
};

export default function PerformanceGraph({
  tickerSymbol,
  inline,
  inlineSwitchCallback,
}: Props): ReactElement {
  const [inlineEnabled, setInlineEnabled] = useState(inline);

  const toggleInlineEnabledWithCallback = () => {
    setInlineEnabled(!inlineEnabled);
    if (inlineSwitchCallback) {
      inlineSwitchCallback(!inlineEnabled);
    }
  };

  // Used to keep track of the last displayed starting price. This helps the interface
  // be less jumpy when switching between prices
  const [lastStartingPrice, setLastStartingPrice] = useState(0);

  // Keep track of current selection, this also sets the default view to 1d
  const [timeSelection, setTimeSelection] = useState<TimeSelection>("1d");

  // This is our hard caching mechanism for the frontend. Each timeframe has an
  // associated set of data that will be checked before going to fetch the data
  // from the backend
  const [pricingState, setPricingState] = useState<PricingStateType>({
    "1d": null,
    "1w": null,
    "1m": null,
    "3m": null,
    "1y": null,
    "5y": null,
  });

  // Fetch current price for displayed price above graph. This can later be refactored out to
  // the global page's price, because we'll be loading it there before reaching this tab
  const quoteQuery = useQueryBundle(GetIexQuote, {
    variables: { ticker: tickerSymbol },
  });

  const [hideDayAndMonthData, setHideDayAndMonthData] = useState(false);
  const [hasLoadedInitialChart, setHasLoadedInitialChart] = useState(false);

  const pricingCallback = useCallback(
    (resultFetch: (data: any) => ParsedDataSpan) => {
      return (data: any) => {
        const parsedData = resultFetch(data);

        setPricingState({
          ...pricingState,
          [timeSelection]: parsedData,
        });

        if (parsedData.data.length === 0) {
          setTimeSelection("1m");
          setHideDayAndMonthData(true);
        } else {
          setHasLoadedInitialChart(true);
        }
      };
    },
    [pricingState, setPricingState, timeSelection]
  );

  if (quoteQuery.state !== "DONE") {
    return <LoadingBlock />;
  }

  // This config is where all configuration options are found for each segment, see `createLineGraphConfig`
  // for more details on customization
  const config = createLineGraphConfig(tickerSymbol);

  const currentConfig = config[timeSelection];
  const currentData = pricingState[timeSelection];
  const currentPrice = quoteQuery.data.iex.quote?.latestPrice || 0;

  const { resultFetch } = currentConfig;

  if (
    !hasLoadedInitialChart &&
    (currentData === null || currentData.data.length === 0)
  ) {
    return (
      <PricingDataLoader
        query={currentConfig.query}
        options={currentConfig.options}
        callBack={pricingCallback(resultFetch)}
      />
    );
  }

  // This is where the pricing data is loaded if our cache is null. See PricingDataLoader for
  // specifics
  if (currentData === null) {
    return (
      <ChartWrapper
        currentPrice={currentPrice}
        startingPrice={lastStartingPrice}
        timeSelection={timeSelection}
        setTimeSelection={setTimeSelection}
        inline={inlineEnabled}
        hideDayAndWeekData={hideDayAndMonthData}
      >
        <PricingDataLoader
          query={currentConfig.query}
          options={currentConfig.options}
          callBack={pricingCallback(resultFetch)}
        />
      </ChartWrapper>
    );
  }

  // After loading a new price, set the last starting price to avoid UI jumpiness
  if (lastStartingPrice !== currentData.starting) {
    setLastStartingPrice(currentData.starting);
  }

  const inlineProperties: Partial<LineSvgProps> = {
    margin: { top: 0, right: 30, bottom: 0, left: 0 },
    axisLeft: null,
    enableCrosshair: false,
    enableGridX: false,
    enableGridY: false,
    enableSlices: false,
  };

  const priceSpan = Math.abs(currentData.max - currentData.min);

  // Display percentage above and below the current displayed range
  const scaleOffset = priceSpan * 0.3;

  const commonProperties: LineSvgProps = {
    margin: { top: 10, right: 10, bottom: 10, left: 70 },
    data: currentData.data,
    colors:
      currentData.starting < currentData.ending
        ? stockIncreaseColor
        : stockDecreaseColor,
    enablePoints: false,

    // Area
    enableArea: false,
    areaOpacity: 0.07,
    areaBaseDatumValue: 400,

    // Scale
    yScale: {
      type: "linear",
      stacked: false,
      min: currentData.min - scaleOffset,
      max: currentData.max + scaleOffset,
    },

    enableGridX: false,

    axisLeft: {
      format: (value) => `${customCurrencyFormat(Number(value))}`,
      tickSize: 0,
    },

    axisBottom: {
      format: () => "",
      tickSize: 0,
    },

    curve: "linear",
    enableCrosshair: true,
    crosshairType: "x",
    useMesh: false,

    animate: false,
    enableSlices: "x",
    sliceTooltip: (props) => {
      const date: Date | null =
        (props.slice.points[0].data as any).date || null;
      const price = (props.slice.points[0].data as any).y;
      return (
        <div className={styles.tooltip}>
          <div>Price: {customCurrencyFormat(price)}</div>
          <div>
            Date:{" "}
            {date !== null
              ? format(date, currentConfig.formatString)
              : "No Date"}
          </div>
        </div>
      );
    },
    ...(inlineEnabled ? inlineProperties : {}),
  };

  return (
    <div>
      <ChartWrapper
        currentPrice={currentPrice}
        startingPrice={currentData.starting || 0}
        timeSelection={timeSelection}
        setTimeSelection={setTimeSelection}
        inline={inlineEnabled}
        hideDayAndWeekData={hideDayAndMonthData}
      >
        <ResponsiveLine {...commonProperties}></ResponsiveLine>
        <div className={styles.intrinio}>
          <a
            className={styles.link}
            href="https://intrinio.com"
            target="_blank"
            rel="noopener noreferrer">
            Source: <img className={styles.logo} src="/images/intrinio-logo.png"/>
          </a>
          </div>
        {/* {inline &&
          (inlineEnabled ? (
            <ExpandOutlined
              className="expandButton"
              onClick={() => toggleInlineEnabledWithCallback()}
            />
          ) : (
            <CompressOutlined
              className="contractButton"
              onClick={() => toggleInlineEnabledWithCallback()}
            />
          ))} */}
      </ChartWrapper>
    </div>
  );
}
